import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Users, Clock, Info, Check, Lock, Globe, Eye, MessageCircle, ListChecks, DollarSign, Percent, ChevronRight, HelpCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { fadeInUp, staggerContainer } from '@/lib/motion'
import { useToast } from '@/components/shared/Toast'
import { useAuthStore } from '@/stores/authStore'

const stakeOptions = ['1/2', '5/10', '25/50', '50/100']
const timerOptions = [15, 20, 30, 45, 60]
const buyInRangePresets = [
  { label: '40-200', min: 40, max: 200 },
  { label: '50-300', min: 50, max: 300 },
  { label: '80-400', min: 80, max: 400 },
  { label: '100-500', min: 100, max: 500 },
]
const BUYIN_FLOOR = 20
const BUYIN_CEIL = 500
const BUYIN_STEP = 10
const BUYIN_MIN_GAP = 20 // minimum gap between min and max in BB

export function CreateRoomScreen() {
  const navigate = useNavigate()
  const { toastRoomCreated } = useToast()
  const isAgent = useAuthStore((s) => s.user?.isAgent ?? false)

  const [step, setStep] = useState<'type' | 'config'>('type')
  const [showHelp, setShowHelp] = useState(false)
  const [roomType, setRoomType] = useState<'public' | 'private' | null>(null)
  const [stakes, setStakes] = useState('1/2')
  const [tableSize, setTableSize] = useState(9)
  const [minBuyInBB, setMinBuyInBB] = useState(100)
  const [maxBuyInBB, setMaxBuyInBB] = useState(200)
  const [turnTimer, setTurnTimer] = useState(30)
  // Private room only options
  const [agentCommission, setAgentCommission] = useState(0)
  const [allowSpectators, setAllowSpectators] = useState(true)
  const [chatEnabled, setChatEnabled] = useState(true)
  const [listedInLobby, setListedInLobby] = useState(true)

  const platformRake = 5
  const bigBlind = parseInt(stakes.split('/')[1])
  const tableSizeLabel = tableSize <= 4 ? '短桌' : tableSize <= 6 ? '中桌' : '滿桌'

  const selectRoomType = (type: 'public' | 'private') => {
    if (type === 'private' && !isAgent) return
    setRoomType(type)
    if (type === 'public') {
      setAgentCommission(0)
      setAllowSpectators(true)
      setChatEnabled(true)
      setListedInLobby(true)
    }
    setStep('config')
  }

  const goBackToType = () => {
    setStep('type')
    setRoomType(null)
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full bg-background"
    >
      {/* Glass Header */}
      <motion.header variants={fadeInUp} className="flex-shrink-0 flex items-center gap-4 px-6 py-4 safe-area-top sticky top-0 bg-background/80 backdrop-blur-xl border-b border-foreground/5 z-40">
        <button
          onClick={() => step === 'config' ? goBackToType() : navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground/5 hover:bg-foreground/10 active:scale-95 transition-all text-foreground/80"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="flex-1 text-xl font-bold text-foreground tracking-wide">
          {step === 'type' ? '創建房間' : roomType === 'public' ? '公開房間設定' : '私人房間設定'}
        </h1>
        <button
          onClick={() => setShowHelp(true)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground/5 hover:bg-foreground/10 active:scale-95 transition-all text-muted-foreground hover:text-foreground/80"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </motion.header>

      {/* Step Indicator */}
      <div className="flex-shrink-0 flex items-center justify-center gap-3 py-3">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors",
            step === 'type' ? "bg-gold text-background" : "bg-gold/20 text-gold"
          )}>1</div>
          <span className={cn("text-xs font-medium transition-colors", step === 'type' ? "text-foreground/70" : "text-foreground/30")}>類型</span>
        </div>
        <div className="w-8 h-px bg-foreground/10" />
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center transition-colors",
            step === 'config' ? "bg-gold text-background" : "bg-foreground/10 text-foreground/30"
          )}>2</div>
          <span className={cn("text-xs font-medium transition-colors", step === 'config' ? "text-foreground/70" : "text-foreground/30")}>設定</span>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {step === 'type' ? (
          /* ==================== STEP 1: Room Type Selection ==================== */
          <motion.main
            key="type-selection"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
            className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide"
          >
            <div className="space-y-5">
              {/* Public Room Card */}
              <button
                onClick={() => selectRoomType('public')}
                className="w-full text-left p-6 rounded-3xl border border-foreground/10 bg-gradient-to-br from-blue-100/80 via-slate-100/60 to-blue-50/80 dark:from-blue-950/30 dark:via-slate-900/40 dark:to-slate-950/30 hover:border-foreground/20 active:scale-[0.98] transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                    <Globe className="w-7 h-7 text-blue-400" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-foreground/20 group-hover:text-muted-foreground transition-colors mt-2" />
                </div>

                <h2 className="text-xl font-bold text-foreground mb-1">公開房間</h2>
                <p className="text-sm text-muted-foreground mb-5">任何人都可以加入的開放牌桌</p>

                <div className="space-y-3">
                  {[
                    { icon: Eye, text: '所有人可觀戰', desc: '開放旁觀' },
                    { icon: Users, text: '任何玩家可加入', desc: '無需邀請' },
                    { icon: ListChecks, text: '顯示於大廳列表', desc: '自動曝光' },
                    { icon: Percent, text: '僅平台抽水 5%', desc: '無額外佣金' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-foreground/5 flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-4 h-4 text-blue-400/70" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-foreground/80 font-medium">{item.text}</span>
                        <span className="text-xs text-foreground/25 ml-2">{item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </button>

              {/* Private Room Card */}
              <button
                onClick={() => selectRoomType('private')}
                disabled={!isAgent}
                className={cn(
                  "w-full text-left p-6 rounded-3xl border transition-all duration-200 group relative overflow-hidden",
                  isAgent
                    ? "border-gold/20 bg-gradient-to-br from-amber-100/80 via-amber-50/60 to-orange-50/80 dark:from-amber-950/30 dark:via-amber-900/10 dark:to-slate-950/30 hover:border-gold/40 active:scale-[0.98]"
                    : "border-foreground/5 bg-foreground/[0.02] cursor-not-allowed"
                )}
              >
                {/* Agent badge */}
                {isAgent && (
                  <div className="absolute top-4 right-4 px-2.5 py-1 rounded-full bg-gold/15 border border-gold/25">
                    <span className="text-[10px] font-bold text-gold uppercase tracking-wider">代理專屬</span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-5">
                  <div className={cn(
                    "w-14 h-14 rounded-2xl border flex items-center justify-center",
                    isAgent
                      ? "bg-gold/10 border-gold/20"
                      : "bg-foreground/5 border-foreground/10"
                  )}>
                    {isAgent
                      ? <Shield className="w-7 h-7 text-gold" />
                      : <Lock className="w-7 h-7 text-foreground/20" />
                    }
                  </div>
                  {isAgent && <ChevronRight className="w-5 h-5 text-foreground/20 group-hover:text-gold/50 transition-colors mt-2" />}
                </div>

                <h2 className={cn("text-xl font-bold mb-1", isAgent ? "text-foreground" : "text-foreground/30")}>私人房間</h2>
                <p className={cn("text-sm mb-5", isAgent ? "text-muted-foreground" : "text-foreground/20")}>
                  {isAgent ? '代理專屬的可定製牌桌' : '需先啟用代理模式才能創建'}
                </p>

                <div className="space-y-3">
                  {[
                    { icon: DollarSign, text: '自訂房主佣金', desc: '0-5% 額外抽水', highlight: true },
                    { icon: Eye, text: '控制觀戰權限', desc: '可關閉旁觀' },
                    { icon: MessageCircle, text: '管理聊天功能', desc: '可開關聊天' },
                    { icon: Shield, text: '房間代碼邀請', desc: '可隱藏於大廳' },
                  ].map((item, i) => (
                    <div key={i} className={cn("flex items-center gap-3", !isAgent && "opacity-30")}>
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        item.highlight && isAgent ? "bg-gold/10" : "bg-foreground/5"
                      )}>
                        <item.icon className={cn(
                          "w-4 h-4",
                          item.highlight && isAgent ? "text-gold" : isAgent ? "text-gold/60" : "text-foreground/20"
                        )} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className={cn("text-sm font-medium", isAgent ? "text-foreground/80" : "text-foreground/30")}>{item.text}</span>
                        <span className={cn("text-xs ml-2", isAgent ? "text-foreground/25" : "text-foreground/15")}>{item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {!isAgent && (
                  <div className="mt-5 pt-4 border-t border-foreground/5 flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5 text-foreground/20" />
                    <span className="text-xs text-foreground/30">前往個人資料啟用代理模式</span>
                  </div>
                )}
              </button>
            </div>
          </motion.main>
        ) : (
          /* ==================== STEP 2: Room Configuration ==================== */
          <motion.main
            key="config-form"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.25 }}
            className="flex-1 overflow-y-auto px-6 py-6 scrollbar-hide space-y-8 pb-32"
          >
            {/* Stakes */}
            <section className="space-y-4">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider pl-1">盲注級別 (Stakes)</label>
              <div className="grid grid-cols-4 gap-2">
                {stakeOptions.map((stake) => {
                  const [sb, bb] = stake.split('/').map(Number)
                  return (
                    <button
                      key={stake}
                      onClick={() => setStakes(stake)}
                      className={cn(
                        'py-2.5 rounded-xl transition-all border flex flex-col items-center gap-0.5',
                        stakes === stake
                          ? 'bg-gold text-background border-gold shadow-lg shadow-gold/20 scale-105'
                          : 'bg-foreground/5 text-foreground/60 border-foreground/5 hover:bg-foreground/10'
                      )}
                    >
                      <span className="text-sm font-bold">${sb}/${bb}</span>
                      <span className={cn("text-[9px] font-medium", stakes === stake ? "text-background/50" : "text-foreground/25")}>
                        小盲/大盲
                      </span>
                    </button>
                  )
                })}
              </div>
            </section>

            {/* Table Settings */}
            <section className="space-y-6 glass-card rounded-3xl p-5">
              {/* Players */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-foreground/80">牌桌人數</label>
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "text-[10px] font-bold px-1.5 py-0.5 rounded",
                      tableSize <= 4 ? "bg-sky-500/15 text-sky-400" : tableSize <= 6 ? "bg-emerald-500/15 text-emerald-400" : "bg-purple-500/15 text-purple-400"
                    )}>{tableSizeLabel}</span>
                    <span className="text-gold font-bold">{tableSize} 人</span>
                  </div>
                </div>
                <input
                  type="range" min={2} max={9} step={1}
                  value={tableSize} onChange={(e) => setTableSize(parseInt(e.target.value))}
                  className="w-full h-2 bg-foreground/10 rounded-full appearance-none accent-gold"
                />
                <div className="flex justify-between text-xs text-foreground/20">
                  <span>2 短桌</span>
                  <span>滿桌 9</span>
                </div>
              </div>

              {/* Timer */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-sm font-medium text-foreground/80">思考時間</label>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="text-gold font-bold">{turnTimer}秒</span>
                  </div>
                </div>
                <div className="scroll-container flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {timerOptions.map(t => (
                    <button
                      key={t}
                      onClick={() => setTurnTimer(t)}
                      className={cn(
                        "flex-shrink-0 w-12 h-8 rounded-lg text-xs font-bold transition-colors border",
                        turnTimer === t ? "bg-gold/20 border-gold text-gold" : "bg-foreground/10 border-foreground/5 text-muted-foreground"
                      )}
                    >
                      {t}s
                    </button>
                  ))}
                </div>
              </div>

              {/* Buy-in Range */}
              <div className="space-y-5 pt-2 border-t border-foreground/5">
                <div className="flex items-baseline justify-between">
                  <label className="text-sm font-medium text-foreground/80">買入範圍</label>
                  <span className="text-[10px] text-foreground/25">1 BB = ${bigBlind} 大盲注</span>
                </div>

                {/* Preset Chips */}
                <div className="flex gap-2 flex-wrap">
                  {buyInRangePresets.map((preset) => {
                    const isActive = minBuyInBB === preset.min && maxBuyInBB === preset.max
                    return (
                      <button
                        key={preset.label}
                        onClick={() => { setMinBuyInBB(preset.min); setMaxBuyInBB(preset.max) }}
                        className={cn(
                          "px-3.5 py-2 rounded-xl text-xs font-bold transition-all border flex flex-col items-center gap-0.5",
                          isActive
                            ? "bg-gold/20 border-gold/30 text-gold shadow-sm shadow-gold/10"
                            : "bg-foreground/10 border-foreground/5 text-muted-foreground active:scale-95"
                        )}
                      >
                        <span>${(preset.min * bigBlind).toLocaleString()}-${(preset.max * bigBlind).toLocaleString()}</span>
                        <span className={cn("text-[9px] font-medium", isActive ? "text-gold/50" : "text-foreground/20")}>{preset.label} BB</span>
                      </button>
                    )
                  })}
                </div>

                {/* Current Values Display */}
                <div className="flex items-center justify-between px-1">
                  <div className="flex flex-col items-start">
                    <span className="text-xs text-muted-foreground">最小買入</span>
                    <span className="text-base text-gold font-bold font-mono">${(minBuyInBB * bigBlind).toLocaleString()}</span>
                    <span className="text-[10px] text-foreground/20 font-mono">{minBuyInBB} BB</span>
                  </div>
                  <div className="w-8 border-t border-foreground/10" />
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-muted-foreground">最大買入</span>
                    <span className="text-base text-gold font-bold font-mono">${(maxBuyInBB * bigBlind).toLocaleString()}</span>
                    <span className="text-[10px] text-foreground/20 font-mono">{maxBuyInBB} BB</span>
                  </div>
                </div>

                {/* Dual-thumb Range Slider */}
                <div className="relative h-10 flex items-center">
                  {/* Track background */}
                  <div className="absolute inset-x-0 h-1.5 bg-foreground/10 rounded-full" />
                  {/* Active range fill */}
                  <div
                    className="absolute h-1.5 bg-gold/50 rounded-full"
                    style={{
                      left: `${((minBuyInBB - BUYIN_FLOOR) / (BUYIN_CEIL - BUYIN_FLOOR)) * 100}%`,
                      right: `${100 - ((maxBuyInBB - BUYIN_FLOOR) / (BUYIN_CEIL - BUYIN_FLOOR)) * 100}%`,
                    }}
                  />
                  {/* Min thumb */}
                  <input
                    type="range"
                    min={BUYIN_FLOOR}
                    max={BUYIN_CEIL}
                    step={BUYIN_STEP}
                    value={minBuyInBB}
                    onChange={(e) => {
                      const v = parseInt(e.target.value)
                      setMinBuyInBB(Math.min(v, maxBuyInBB - BUYIN_MIN_GAP))
                    }}
                    className="absolute inset-x-0 w-full h-10 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-gold [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-gold/30 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing [&::-webkit-slider-thumb]:active:scale-110 [&::-webkit-slider-thumb]:transition-transform"
                    style={{ zIndex: minBuyInBB > BUYIN_CEIL - BUYIN_MIN_GAP ? 5 : 3 }}
                  />
                  {/* Max thumb */}
                  <input
                    type="range"
                    min={BUYIN_FLOOR}
                    max={BUYIN_CEIL}
                    step={BUYIN_STEP}
                    value={maxBuyInBB}
                    onChange={(e) => {
                      const v = parseInt(e.target.value)
                      setMaxBuyInBB(Math.max(v, minBuyInBB + BUYIN_MIN_GAP))
                    }}
                    className="absolute inset-x-0 w-full h-10 appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-foreground [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-foreground/80 [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-foreground/20 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:active:cursor-grabbing [&::-webkit-slider-thumb]:active:scale-110 [&::-webkit-slider-thumb]:transition-transform"
                    style={{ zIndex: 4 }}
                  />
                </div>

                {/* Scale labels */}
                <div className="flex justify-between text-[10px] text-foreground/20 font-mono px-1 -mt-2">
                  <span>${BUYIN_FLOOR * bigBlind}</span>
                  <span>${(BUYIN_CEIL * bigBlind).toLocaleString()}</span>
                </div>
              </div>
            </section>

            {/* Private Room Options — only visible for private rooms */}
            {roomType === 'private' && (
              <section className="space-y-4">
                <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider pl-1">私人房設定</label>

                <div className="space-y-3">
                  {[
                    { label: '允許觀戰', desc: '關閉後僅玩家可見', state: allowSpectators, setState: setAllowSpectators },
                    { label: '開啟聊天', desc: '玩家間文字聊天', state: chatEnabled, setState: setChatEnabled },
                    { label: '顯示於大廳', desc: '關閉後需房間代碼加入', state: listedInLobby, setState: setListedInLobby },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-foreground/5 border border-foreground/5">
                      <div>
                        <span className="text-sm font-medium text-foreground/80 block">{item.label}</span>
                        <span className="text-[11px] text-foreground/30">{item.desc}</span>
                      </div>
                      <button
                        onClick={() => item.setState(!item.state)}
                        className={cn(
                          "w-12 h-7 rounded-full transition-colors relative flex-shrink-0",
                          item.state ? "bg-gold" : "bg-foreground/10"
                        )}
                      >
                        <div className={cn(
                          "absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300",
                          item.state ? "left-6" : "left-1"
                        )} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Agent Commission */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-100/50 to-purple-100/50 dark:from-indigo-900/10 dark:to-purple-900/10 border border-foreground/5 mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground/90">抽水設定</span>
                    <Info className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
                    <span>平台抽水 (固定)</span>
                    <span className="text-foreground font-bold">{platformRake}%</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground mb-4">
                    <span>房主佣金</span>
                    <span className="text-gold font-bold">{agentCommission}%</span>
                  </div>
                  <div className="relative pt-1 pb-5">
                    <input
                      type="range" min={0} max={5} step={1}
                      value={agentCommission}
                      onChange={(e) => setAgentCommission(parseInt(e.target.value))}
                      className="w-full h-1.5 bg-foreground/10 rounded-full appearance-none accent-gold relative z-10"
                    />
                    {/* Tick marks */}
                    <div className="absolute left-0 right-0 top-[11px] flex justify-between px-[2px] pointer-events-none">
                      {[0, 1, 2, 3, 4, 5].map(v => (
                        <div key={v} className="flex flex-col items-center gap-1.5">
                          <div className={cn(
                            "w-1.5 h-1.5 rounded-full transition-colors",
                            v <= agentCommission ? "bg-gold" : "bg-foreground/20"
                          )} />
                          <span className={cn(
                            "text-[9px] font-mono transition-colors",
                            v === agentCommission ? "text-gold font-bold" : "text-foreground/20"
                          )}>{v}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-foreground/30 pt-3 border-t border-foreground/5">
                    <span>玩家總抽水</span>
                    <span className="text-foreground font-bold">{platformRake + agentCommission}%</span>
                  </div>
                </div>
              </section>
            )}
          </motion.main>
        )}
      </AnimatePresence>

      {/* Footer Actions — only show on config step */}
      {step === 'config' && (
        <motion.footer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-shrink-0 safe-area-bottom px-6 py-4 bg-background/90 backdrop-blur-xl border-t border-foreground/5 z-50"
        >
          {/* Settings Summary */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            <div className="text-center">
              <span className="text-[10px] text-foreground/30 block">盲注</span>
              <span className="text-xs text-foreground font-bold">${stakes}</span>
            </div>
            <div className="text-center">
              <span className="text-[10px] text-foreground/30 block">人數</span>
              <span className="text-xs text-foreground font-bold">{tableSize}人</span>
            </div>
            <div className="text-center">
              <span className="text-[10px] text-foreground/30 block">思考</span>
              <span className="text-xs text-foreground font-bold">{turnTimer}s</span>
            </div>
            <div className="text-center">
              <span className="text-[10px] text-foreground/30 block">買入</span>
              <span className="text-xs text-gold font-bold">${(bigBlind * minBuyInBB).toLocaleString()}-{(bigBlind * maxBuyInBB).toLocaleString()}</span>
            </div>
          </div>
          {roomType === 'private' && agentCommission > 0 && (
            <div className="flex items-center justify-center gap-1.5 mb-3">
              <Percent className="w-3 h-3 text-foreground/30" />
              <span className="text-[10px] text-muted-foreground">總抽水 {platformRake + agentCommission}%（平台 {platformRake}% + 佣金 {agentCommission}%）</span>
            </div>
          )}
          <button
            onClick={() => {
              const roomCode = `PWG${Math.random().toString(36).substring(2, 5).toUpperCase()}`
              toastRoomCreated(roomCode)
              navigate(`/room/${roomCode}`)
            }}
            className="w-full h-14 btn-primary rounded-2xl text-lg tracking-wide flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            立即創建
          </button>
        </motion.footer>
      )}
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex items-end justify-center"
            onClick={() => setShowHelp(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal Sheet */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-h-[85vh] bg-background border-t border-foreground/10 rounded-t-3xl overflow-hidden flex flex-col"
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 rounded-full bg-foreground/15" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-3 border-b border-foreground/5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-gold/10 flex items-center justify-center">
                    <HelpCircle className="w-4 h-4 text-gold" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">房間設定說明</h2>
                </div>
                <button
                  onClick={() => setShowHelp(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-foreground/5 hover:bg-foreground/10 active:scale-95 transition-all text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 scrollbar-hide safe-area-bottom">

                {/* Stakes */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gold" />
                    <h3 className="text-sm font-bold text-foreground">盲注級別 (Stakes)</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    盲注是每手牌的強制下注金額，以「小盲/大盲」表示。例如 <span className="text-foreground/80 font-medium">1/2</span> 表示小盲 $1、大盲 $2。盲注越高，遊戲的最低資金門檻也越高。
                  </p>
                </div>

                <div className="border-t border-foreground/5" />

                {/* BB Explanation */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-400" />
                    <h3 className="text-sm font-bold text-foreground">什麼是 BB？</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    BB 是「Big Blind（大盲注）」的縮寫，用來衡量籌碼數量。例如在 <span className="text-foreground/80 font-medium">1/2</span> 的牌桌上：
                  </p>
                  <div className="bg-foreground/[0.03] rounded-xl p-3 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">100 BB =</span>
                      <span className="text-gold font-bold">100 x $2 = $200</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">200 BB =</span>
                      <span className="text-gold font-bold">200 x $2 = $400</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    當你切換不同盲注級別時，同樣的 BB 數量代表的金額也會隨之改變。
                  </p>
                </div>

                <div className="border-t border-foreground/5" />

                {/* Buy-in Range */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <ListChecks className="w-4 h-4 text-emerald-400" />
                    <h3 className="text-sm font-bold text-foreground">買入範圍</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    玩家入座時需要帶入的籌碼範圍。<span className="text-foreground/80 font-medium">最小買入</span>是入座的最低金額，<span className="text-foreground/80 font-medium">最大買入</span>是入座的上限。建議設定合理範圍，讓不同資金的玩家都能參與。
                  </p>
                </div>

                <div className="border-t border-foreground/5" />

                {/* Table Size */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-400" />
                    <h3 className="text-sm font-bold text-foreground">牌桌人數</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    每桌最多可容納的玩家數。<span className="text-foreground/80 font-medium">2-4人</span>為短桌（節奏快、對抗性強），<span className="text-foreground/80 font-medium">5-6人</span>為中桌，<span className="text-foreground/80 font-medium">7-9人</span>為滿桌（策略性更強、等待時間較長）。
                  </p>
                </div>

                <div className="border-t border-foreground/5" />

                {/* Timer */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-sky-400" />
                    <h3 className="text-sm font-bold text-foreground">思考時間</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    每位玩家每次行動的最大思考時間。超時將自動棄牌（Fold）。<span className="text-foreground/80 font-medium">15-20秒</span>適合快速遊戲，<span className="text-foreground/80 font-medium">30秒</span>為標準設定，<span className="text-foreground/80 font-medium">45-60秒</span>適合深度策略遊戲。
                  </p>
                </div>

                <div className="border-t border-foreground/5" />

                {/* Private Room Options */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gold" />
                    <h3 className="text-sm font-bold text-foreground">私人房間選項</h3>
                    <span className="text-[9px] font-bold text-gold/70 bg-gold/10 px-1.5 py-0.5 rounded-full">代理專屬</span>
                  </div>
                  <div className="space-y-3 mt-1">
                    <div>
                      <p className="text-xs text-foreground/70 font-medium">允許觀戰</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">開啟後非參與玩家可觀看牌局。關閉則僅入座玩家可見桌面。</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground/70 font-medium">開啟聊天</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">允許牌桌內玩家文字聊天互動。</p>
                    </div>
                    <div>
                      <p className="text-xs text-foreground/70 font-medium">顯示於大廳</p>
                      <p className="text-[11px] text-muted-foreground leading-relaxed">開啟後房間會出現在公開大廳列表。關閉後玩家需要房間代碼才能加入。</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-foreground/5" />

                {/* Commission */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-rose-400" />
                    <h3 className="text-sm font-bold text-foreground">抽水說明</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    每手牌結算時，平台會從底池中抽取一定比例作為服務費。
                  </p>
                  <div className="bg-foreground/[0.03] rounded-xl p-3 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">平台抽水（固定）</span>
                      <span className="text-foreground font-bold">5%</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">房主佣金（代理可設）</span>
                      <span className="text-gold font-bold">0-5%</span>
                    </div>
                    <div className="border-t border-foreground/5 pt-2 flex justify-between text-xs">
                      <span className="text-muted-foreground">玩家最高總抽水</span>
                      <span className="text-foreground font-bold">最多 10%</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    公開房間僅收取平台抽水。私人房間的代理可額外設定佣金，作為經營房間的收入。
                  </p>
                </div>

                {/* Bottom spacing */}
                <div className="h-4" />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
